<template>
  <div :class="filterExpanded ? 'b-contracts__filter' : 'b-contracts__filter collapsed'">
    <div class="row px-6 py-4" style="align-items: flex-end">
      <div class="col-2 b-contracts__date">
        <label>{{ $t('CONTENT.contract.contract_date') }}:</label>
        <datepicker
          class="b-datepicker"
          v-model="startDate"
          :formatter="datepickerFormat"
          :disabled-date="maxDateForStartDateFn"
        ></datepicker>
      </div>
      <div class="col-2 b-contracts__date">
        <datepicker
          class="b-datepicker"
          v-model="endDate"
          :formatter="datepickerFormat"
          :disabled-date="minDateForEndDateFn"
        ></datepicker>
      </div>
      <template v-if="currentContractType !== 'alternative'">
        <div class="col-2">
          <label>{{ $t('CONTENT.contract.status_text') }}</label>
          <clearable-select
            v-model="status"
            :list="statuses"
            option-value="value"
            option-text="name"
            :placeholder="$t('CONTENT.choose')"
          />
        </div>
        <div class="col-1 b-contracts__date">
          <label>{{ $t('CONTENT.contract.contract_period') }}:</label>
          <datepicker
            class="b-datepicker"
            v-model="havePaymentsFrom"
            :formatter="datepickerFormat"
            :disabled-date="maxDateForHavePaymentsFromFn"
          />
        </div>
        <div class="col-1 b-contracts__date">
          <datepicker
            class="b-datepicker"
            v-model="havePaymentsTo"
            :formatter="datepickerFormat"
            :disabled-date="minDateForHavePaymentsToFn"
          />
        </div>
        <div class="col-2">
          <label>{{ $t('CONTENT.contract.indexation_period') }}:</label>
          <clearable-select v-model="indexationMonth"
                            :list="indexationMonths"
                            option-value="value"
                            option-text="name"
                            :placeholder="$t('CONTENT.choose')"/>
        </div>
        <div class="col-2">
          <numeric class="b-numeric" controls :min="1990" v-model="indexationYear"
                   @keypress="changeNumericValue"/>
        </div>
      </template>
    </div>
    <div class="row px-6 py-4" style="align-items: flex-end">
      <div class="col-3">
        <label>{{ $t('CONTENT.searchByContract') }}:</label>
        <input type="text" class="b-input" v-model.lazy="filterParams.searchQuery"
               :placeholder="$t('CONTENT.search')">
      </div>

      <div class="col-3">
        <label>{{ $t('CONTENT.searchByLessee') }}:</label>
        <clearable-select
          v-model.lazy="filterParams.lesseeId"
          :list="allCounterparties"
          option-value="idInDB"
          option-text="displayName"
          :placeholder="$t('CONTENT.choose')"
        />
      </div>

      <div class="col-3">
        <label>{{ $t('CONTENT.searchByLessor') }}:</label>
        <clearable-select
          v-model.lazy="filterParams.lessorId"
          :list="allCounterparties"
          option-value="idInDB"
          option-text="displayName"
          :placeholder="$t('CONTENT.choose')"
        />
      </div>
      <div class="col-3">
        <button
          @click="resetFilter"
          class="b-btn">
          <dynamic-icon
            class="b-btn__icon"
            icon="close"/>
          {{ $t('CONTENT.contract.reset_filter') }}
        </button>
      </div>
    </div>
    <button type="button" class="b-btn b-btn_border_none toggle-filter-btn" @click="toggleFilter">
      <span>Поиск</span>
      <dynamic-icon :icon="filterExpanded ? 'arrow-down' : 'arrow-right'"/>
    </button>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import dateConverter from "../../../utils/converters/date.converter";
import datepickerFormat from '@/mixins/datepickerFormat.mixin';
import changeNumericValue from "../../../mixins/changeNumericValue";
import ClearableSelect from "@/components/clearableSelect/ClearableSelect.vue";

export default {
  props: {
    currentContractType: {
      type: String,
    },
    filterParams: {
      type: Object,
    }
  },
  created() {
    if (this.filterParams.indexationMonthAndYear !== null) {
      const dateAsDate = dateConverter.convertToDate(this.filterParams.indexationMonthAndYear);
      this.indexationMonth = this.$moment(dateAsDate).format('MM');
      this.indexationYear = +this.$moment(dateAsDate).format('YYYY');
    }
  },
  mixins: [datepickerFormat, changeNumericValue],
  components: {'clearable-select': ClearableSelect},
  data() {
    return {
      maxDateForStartDate: null,
      minDateForEndDate: null,
      maxDateForHavePaymentsFrom: null,
      minDateForHavePaymentsTo: null,
      status: '',
      indexationMonth: '',
      indexationYear: 2020,
      startDateForFilter: new Date(1900, 1, 1),
      endDateForFilter: new Date(2500, 1, 1),
      filterExpanded: true
    }
  },
  mounted() {
    const filterExpanded = localStorage.getItem('lat_contracts_filter_expanded')
    this.filterExpanded = filterExpanded === 'true'
    this.setFilterParams()
  },
  watch: {
    filterParams: {
      handler() {
        this.filterContractsByParams();
      },
      deep: true,
    },
    'filterParams.searchQuery'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.filterParams.page = 1;
      }
    },
    status() {
      this.setStatus();
    },
    'indexationMonth'() {
      this.setIndexationDate();
    },
    'indexationYear'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.setIndexationDate();
      }
    },
  },
  computed: {
    ...mapGetters(['allCounterparties']),
    statuses() {
      return [
        { value: 1, name: `${this.$t('CONTENT.contract_status.status_project')}` },
        { value: 3, name: `${this.$t('CONTENT.contract_status.status_modified')}` },
        { value: 2, name: `${this.$t('CONTENT.contract_status.status_on_approval_create')}` },
        { value: 4, name: `${this.$t('CONTENT.contract_status.status_on_approval_modified')}` },
        { value: 5, name: `${this.$t('CONTENT.contract_status.status_active')}` },
      ];
    },
    indexationMonths() {
      return [
        {value: '01', name: this.$t('CONTENT.months.jan')},
        {value: '02', name: this.$t('CONTENT.months.feb')},
        {value: '03', name: this.$t('CONTENT.months.mar')},
        {value: '04', name: this.$t('CONTENT.months.apr')},
        {value: '05', name: this.$t('CONTENT.months.may')},
        {value: '06', name: this.$t('CONTENT.months.jun')},
        {value: '07', name: this.$t('CONTENT.months.jul')},
        {value: '08', name: this.$t('CONTENT.months.aug')},
        {value: '09', name: this.$t('CONTENT.months.sept')},
        {value: '10', name: this.$t('CONTENT.months.oct')},
        {value: '11', name: this.$t('CONTENT.months.nov')},
        {value: '12', name: this.$t('CONTENT.months.dec')}
      ]
    },
    startDate: {
      get() {
        if (this.filterParams.startDate !== null) {
          const convertedStringDate = decodeURIComponent(
            this.filterParams.startDate
          );
          return dateConverter.convertToDate(convertedStringDate);
        }

        return null;
      },
      set(newVal) {
        if (newVal !== null) {
          let dateInStringFormat = dateConverter.convertToString(newVal);
          this.filterParams.startDate = encodeURIComponent(dateInStringFormat);
          this.minDateForEndDate = newVal;
          this.filterParams.page = 1;
          this.minDateForEndDateFn();
        } else {
          this.minDateForEndDate = null;
          this.filterParams.startDate = null;
        }
        this.filterParams.page = 1;
      }
    },
    endDate: {
      get() {
        if (this.filterParams.endDate !== null) {
          const convertedStringDate = decodeURIComponent(
            this.filterParams.endDate
          );
          return dateConverter.convertToDate(convertedStringDate);
        }

        return null;
      },
      set(newVal) {
        if (newVal !== null) {
          let dateInStringFormat = dateConverter.convertToString(newVal);
          this.filterParams.endDate = encodeURIComponent(dateInStringFormat);
          this.maxDateForStartDate = newVal;
          this.filterParams.page = 1;
          this.maxDateForStartDateFn();
        } else {
          this.maxDateForStartDate = null;
          this.filterParams.endDate = null;
        }
        this.filterParams.page = 1;
      }
    },
    havePaymentsFrom: {
      get() {
        if (this.filterParams.havePaymentsFrom !== null) {
          const convertedStringDate = decodeURIComponent(
            this.filterParams.havePaymentsFrom
          );
          return dateConverter.convertToDate(convertedStringDate);
        }

        return null;
      },
      set(newVal) {
        if (newVal !== null) {
          let dateInStringFormat = dateConverter.convertToString(newVal);
          this.filterParams.havePaymentsFrom = encodeURIComponent(dateInStringFormat);
          this.minDateForHavePaymentsTo = newVal;
          this.filterParams.page = 1;
          this.minDateForHavePaymentsToFn();
        } else {
          this.minDateForHavePaymentsTo = null;
          this.filterParams.havePaymentsFrom = null;
        }
        this.filterParams.page = 1;
      }
    },
    havePaymentsTo: {
      get() {
        if (this.filterParams.havePaymentsTo !== null) {
          const convertedStringDate = decodeURIComponent(
            this.filterParams.havePaymentsTo
          );
          return dateConverter.convertToDate(convertedStringDate);
        }

        return null;
      },
      set(newVal) {
        if (newVal !== null) {
          let dateInStringFormat = dateConverter.convertToString(newVal);
          this.filterParams.havePaymentsTo = encodeURIComponent(dateInStringFormat);
          this.maxDateForHavePaymentsFrom = newVal;
          this.filterParams.page = 1;
          this.maxDateForHavePaymentsFromFn();
        } else {
          this.maxDateForHavePaymentsFrom = null;
          this.filterParams.havePaymentsTo = null;
        }
        this.filterParams.page = 1;
      }
    },
  },
  methods: {
    setFilterParams() {
      if (this.filterParams.status === null || this.filterParams.status === undefined) this.status = ''
      else this.status = this.filterParams.status;

      if (this.filterParams.indexationMonthAndYear !== undefined && this.filterParams.indexationMonthAndYear !== null) {
        const date = this.filterParams.indexationMonthAndYear.split('/')
        this.indexationMonth = date[1]
        this.indexationYear = parseInt(date[2])
      }
    },
    maxDateForStartDateFn(date) {
      if (this.maxDateForStartDate !== null) {
        return date > this.maxDateForStartDate;
      }
    },
    minDateForEndDateFn(date) {
      if (this.minDateForEndDate !== null) {
        return date < this.minDateForEndDate;
      }
    },
    maxDateForHavePaymentsFromFn(date) {
      if (this.maxDateForHavePaymentsFrom !== null) {
        return date > this.maxDateForHavePaymentsFrom;
      }
    },
    minDateForHavePaymentsToFn(date) {
      if (this.minDateForHavePaymentsTo !== null) {
        return date < this.minDateForHavePaymentsTo;
      }
    },
    setIndexationDate() {
      if (this.indexationMonth !== '') {
        this.filterParams.indexationMonthAndYear = `01/${this.indexationMonth}/${this.indexationYear}/LeaseProDateWithoutTime`;
      } else {
        this.filterParams.indexationMonthAndYear = null;
      }
      this.filterParams.page = 1;
    },
    setStatus() {
      if (this.status !== '') {
        this.$set(this.filterParams, 'status', this.status)
      } else {
        this.$set(this.filterParams, 'status', null)
      }
      this.filterParams.page = 1;
    },
    filterContractsByParams() {
      this.$emit('setFilterParams');
    },
    resetFilter() {
      this.status = '';
      this.indexationMonth = '';
      this.indexationYear = 2020;
      this.$emit('resetFilter');
    },
    toggleFilter() {
      this.filterExpanded = !this.filterExpanded
      localStorage.setItem('lat_contracts_filter_expanded', this.filterExpanded)
    }
  }
}
</script>
